import { apiFetch, getAccessToken } from './client';

export interface WebChatMessage {
  id: number;
  ticket_id: number;
  sender_type: 'customer' | 'agent' | 'system';
  content: string;
  message_type: string;
  created_at: string;
}

export interface WebChatSession {
  ticketId: number;
  sessionToken: string;
  status: string;
  isActive: boolean;
  awaitingRating?: boolean;
  customerRating?: string | null;
  customerName?: string | null;
  messages: WebChatMessage[];
  linkedToAccount?: boolean;
  guestPin?: string | null;
}

export interface WebChatResumeInfo {
  ticketId: number;
  status: string;
  requiresLogin: boolean;
  requiresPin: boolean;
  canResume: boolean;
  customerName?: string | null;
}

async function webChatFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return apiFetch<T>(path, { ...options, headers });
}

export interface WebChatPollResult {
  messages: WebChatMessage[];
  status: string;
  isActive: boolean;
  awaitingRating?: boolean;
  customerRating?: string | null;
}

const SESSION_KEY = 'hosty_web_chat_session';

export function getStoredSessionToken(): string | null {
  return localStorage.getItem(SESSION_KEY);
}

export function setStoredSessionToken(token: string | null): void {
  if (token) {
    localStorage.setItem(SESSION_KEY, token);
  } else {
    localStorage.removeItem(SESSION_KEY);
  }
}

export async function getSupportHours(): Promise<{ outsideHours: boolean }> {
  try {
    const result = await apiFetch<{ success: boolean; outsideHours: boolean }>(
      '/api/web-chat/support-hours'
    );
    return { outsideHours: Boolean(result.outsideHours) };
  } catch {
    return { outsideHours: false };
  }
}

export async function startWebChat(data: {
  message: string;
  customerName?: string;
  customerEmail?: string;
  sessionToken?: string | null;
  urgent?: boolean;
}): Promise<WebChatSession> {
  const result = await webChatFetch<{
    success: boolean;
    ticketId: number;
    sessionToken: string;
    status: string;
    messages: WebChatMessage[];
    linkedToAccount?: boolean;
    guestPin?: string | null;
  }>('/api/web-chat/start', {
    method: 'POST',
    body: JSON.stringify({
      message: data.message,
      customerName: data.customerName,
      customerEmail: data.customerEmail,
      urgent: data.urgent ?? false,
      sessionToken: data.sessionToken ?? undefined,
    }),
  });

  setStoredSessionToken(result.sessionToken);

  return {
    ticketId: result.ticketId,
    sessionToken: result.sessionToken,
    status: result.status,
    isActive: !['awaiting_customer_approval', 'closed', 'cancelled', 'resolved'].includes(result.status),
    messages: result.messages,
    linkedToAccount: result.linkedToAccount,
    guestPin: result.guestPin ?? null,
  };
}

export async function getWebChatResumeInfo(ticketId: number): Promise<WebChatResumeInfo | null> {
  try {
    const result = await apiFetch<{
      success: boolean;
      ticketId: number;
      status: string;
      requiresLogin: boolean;
      requiresPin: boolean;
      canResume: boolean;
      customerName?: string | null;
    }>(`/api/web-chat/resume-info?ticketId=${ticketId}`);
    return {
      ticketId: result.ticketId,
      status: result.status,
      requiresLogin: result.requiresLogin,
      requiresPin: result.requiresPin,
      canResume: result.canResume,
      customerName: result.customerName,
    };
  } catch {
    return null;
  }
}

export async function resumeWebChatWithAccount(ticketId: number): Promise<WebChatSession | null> {
  try {
    const result = await webChatFetch<{
      success: boolean;
      ticketId: number;
      sessionToken: string;
      status: string;
      isActive?: boolean;
      awaitingRating?: boolean;
      customerRating?: string | null;
      customerName?: string | null;
      messages: WebChatMessage[];
    }>('/api/web-chat/resume-account', {
      method: 'POST',
      body: JSON.stringify({ ticketId }),
    });
    setStoredSessionToken(result.sessionToken);
    return mapSession(result);
  } catch {
    return null;
  }
}

export async function resumeWebChatWithPin(
  ticketId: number,
  pin: string
): Promise<WebChatSession | null> {
  try {
    const result = await apiFetch<{
      success: boolean;
      ticketId: number;
      sessionToken: string;
      status: string;
      isActive?: boolean;
      awaitingRating?: boolean;
      customerRating?: string | null;
      customerName?: string | null;
      messages: WebChatMessage[];
    }>('/api/web-chat/resume-pin', {
      method: 'POST',
      body: JSON.stringify({ ticketId, pin }),
    });
    setStoredSessionToken(result.sessionToken);
    return mapSession(result);
  } catch {
    return null;
  }
}

function mapSession(result: {
  ticketId: number;
  sessionToken: string;
  status: string;
  isActive?: boolean;
  awaitingRating?: boolean;
  customerRating?: string | null;
  customerName?: string | null;
  messages: WebChatMessage[];
}): WebChatSession {
  return {
    ticketId: result.ticketId,
    sessionToken: result.sessionToken,
    status: result.status,
    isActive: result.isActive ?? !['awaiting_customer_approval', 'closed', 'cancelled', 'resolved'].includes(result.status),
    awaitingRating: result.awaitingRating,
    customerRating: result.customerRating ?? null,
    customerName: result.customerName,
    messages: result.messages,
  };
}

export async function resumeWebChat(sessionToken: string): Promise<WebChatSession | null> {
  try {
    const result = await apiFetch<{
      success: boolean;
      ticketId: number;
      sessionToken: string;
      status: string;
      isActive?: boolean;
      awaitingRating?: boolean;
      customerRating?: string | null;
      customerName?: string | null;
      messages: WebChatMessage[];
    }>(`/api/web-chat/session?sessionToken=${encodeURIComponent(sessionToken)}`);

    return mapSession(result);
  } catch {
    setStoredSessionToken(null);
    return null;
  }
}

export async function sendWebChatMessage(
  sessionToken: string,
  content: string
): Promise<WebChatMessage> {
  const result = await apiFetch<{
    success: boolean;
    message: WebChatMessage;
  }>('/api/web-chat/message', {
    method: 'POST',
    body: JSON.stringify({ sessionToken, content }),
  });
  return result.message;
}

export async function pollWebChatSession(
  sessionToken: string,
  sinceId?: number
): Promise<WebChatPollResult | null> {
  const query = new URLSearchParams({ sessionToken });
  if (sinceId) query.set('sinceId', String(sinceId));

  try {
    const result = await apiFetch<{
      success: boolean;
      messages: WebChatMessage[];
      status: string;
      isActive?: boolean;
      awaitingRating?: boolean;
      customerRating?: string | null;
    }>(`/api/web-chat/messages?${query.toString()}`);

    return {
      messages: result.messages,
      status: result.status,
      isActive: result.isActive ?? !['awaiting_customer_approval', 'closed', 'cancelled', 'resolved'].includes(result.status),
      awaitingRating: result.awaitingRating,
      customerRating: result.customerRating ?? null,
    };
  } catch {
    return null;
  }
}

export async function rateWebChatSession(
  sessionToken: string,
  rating: string,
  feedback?: string
): Promise<WebChatSession> {
  const result = await apiFetch<{
    success: boolean;
    ticketId: number;
    sessionToken: string;
    status: string;
    awaitingRating: boolean;
    customerRating: string;
    messages: WebChatMessage[];
  }>('/api/web-chat/rate', {
    method: 'POST',
    body: JSON.stringify({ sessionToken, rating, feedback }),
  });

  return {
    ticketId: result.ticketId,
    sessionToken: result.sessionToken,
    status: result.status,
    isActive: false,
    awaitingRating: result.awaitingRating,
    customerRating: result.customerRating,
    messages: result.messages,
  };
}
